/**
 * 인계된 수강권의 환불 후보 선택.
 *
 * 담당강사 인계는 정규 운영 절차이므로, 인계됐다는 이유로 환불 동선이 달라져서는 안 된다.
 * 다만 인계본은 결제 원장이 원판매 수강권에 남아 자기 `totalPrice` 가 0이다. 그래서 구 환불
 * API 로는 계산이 안 되고, 결제행 단위인 신규 API 를 써야 한다.
 *
 * 어느 결제행을 고르느냐가 곧 **어느 강사·판매담당자의 매출을 취소하느냐**이므로 프론트가
 * 날짜나 순서로 추측하지 않는다. 서버(`membership.refundCandidates`)가 계산한 것만 쓴다.
 *
 * M3′/P4′(소프트 삭제 개방 패키지) — 인계 계보가 없어도 "삭제 원본"이거나 "탈퇴 회원"이면
 * 같은 이유로 신규 API 를 강제한다. 백엔드가 단일 세대에도 `refundCandidates` 를 항상
 * 병기하게 됐으므로(B10), 이 파일의 게이트를 `hasHandoffLineage` 하나로만 두면 삭제
 * 원본·탈퇴 회원의 단일 세대 수강권이 여전히 후보를 무시하고 구 PATCH 환불로 빠진다 —
 * 그 경로는 서버가 `MEMBERSHIP_LIFECYCLE_ENDED`(삭제 원본) 또는 탈퇴 가드로 거절한다.
 * 모바일 관리자 웹(`admin_mb_web/src/features/sharedMembership/refundCandidates.ts`)과 반드시
 * 같은 규칙을 유지한다 — 한쪽만 고치면 같은 수강권이 두 화면에서 다르게 동작한다.
 */

import {
    MEMBERSHIP_LIFECYCLE_ENDED_ERROR_CODE,
    MEMBERSHIP_LIFECYCLE_ENDED_MESSAGE,
    REFUND_ROW_IMMUTABLE_ERROR_CODE,
    REFUND_ROW_IMMUTABLE_MESSAGE,
} from '../../lib/membershipLifecycle';
import { isMemberRecordReadOnly } from '../publicMember/types';

export type RefundCandidate = {
    membershipPaymentId?: string;
    membershipId?: string;
    isCurrentMembershipRow?: boolean;
    saleStatus?: string;
    paymentDate?: string;
    paymentAmount?: number;
    totalSession?: number;
    refundableAmount?: number;
    coachId?: string;
    coachName?: string;
    salesManager?: { userName?: string; name?: string } | string | null;
    currentMembershipId?: string;
    canRefund?: boolean;
    blockReason?: string;
};

/** 서버 blockReason 을 사람 말로. 매출 목록과 같은 어휘를 쓴다. */
export const getRefundBlockMessage = (blockReason?: string): string => {
    switch (blockReason) {
        case MEMBERSHIP_LIFECYCLE_ENDED_ERROR_CODE:
            return MEMBERSHIP_LIFECYCLE_ENDED_MESSAGE;
        case REFUND_ROW_IMMUTABLE_ERROR_CODE:
            return REFUND_ROW_IMMUTABLE_MESSAGE;
        case 'REQUIRED_INDEXES_NOT_READY':
            return '서버 준비가 완료되지 않아 환불할 수 없습니다. 잠시 후 다시 시도해주세요.';
        case 'NO_REFUNDABLE_AMOUNT':
            return '환불 가능한 금액이 없습니다.';
        case 'CURRENT_MEMBERSHIP_UNRESOLVED':
            return '현재 수강권 연결을 확인하지 못해 환불이 잠겨 있습니다. 운영팀 확인이 필요합니다.';
        case 'SHARED_MEMBERSHIP_REQUIRES_MEMBERSHIP_REFUND_FLOW':
            // 신규 API 는 활성 공유를 거부하고, 공유 회계는 인계 계보를 모른다.
            // 원판매 잔액은 공유를 종료해야 처리할 수 있다.
            return '공유 중인 수강권입니다. 공유를 종료한 뒤 환불하거나 운영팀 확인이 필요합니다.';
        case 'NOT_ORIGINAL_SALES_ROW':
            return '환불할 수 있는 결제 건이 아닙니다.';
        default:
            return '이 수강권은 환불할 수 없습니다. 운영팀 확인이 필요합니다.';
    }
};

export const getSalesManagerName = (candidate: RefundCandidate): string => {
    const manager = candidate?.salesManager;
    if (!manager) return '';
    if (typeof manager === 'string') return manager;
    return manager.userName || manager.name || '';
};

export type RefundModalProps = {
    salesRecordId?: string;
    maxRefundAmountOverride?: number;
    salesAttributionName?: string;
    refundBlockMessage?: string;
    refundCandidates?: RefundCandidate[];
};

/**
 * 상세 화면이 환불 모달에 넘길 값을 정한다.
 *
 * - 후보 자체가 없다 → 인계 이력이 없는 평범한 수강권. 기존 동작 그대로(빈 객체).
 * - 환불 가능한 후보 1개 → 자동 선택.
 * - 여러 개 → 화면이 고르게 한다. 자동으로 정하지 않는다(귀속이 달라진다).
 * - 0개 → 왜 못 하는지 안내한다.
 *
 * `options.isGroup` — 그룹(회원권 티켓)은 이 백엔드 변경(2026-08-25 소프트 삭제 개방
 * 패키지)에 포함되지 않는다. `deletedContext`/`refundCandidates` 를 내려주지 않고, 탈퇴
 * 가드도 그룹 환불 라우트(`memberTicket.route.ts`)에는 그대로 남아 있다. `isReadOnly` 는
 * 회원 단위 값이라 그룹 화면에도 그대로 실리므로, 여기서 명시적으로 걸러내지 않으면
 * 탈퇴 회원의 그룹 티켓에서 "환불 가능 매출을 확인하지 못했습니다"로 잘못 막힌다 — 원래는
 * 서버가 `WITHDRAWN_MEMBER_READ_ONLY` 로 정확히 거절해야 하는 경로다.
 */
export const buildRefundModalProps = (
    membership: any,
    options?: { isGroup?: boolean },
): RefundModalProps => {
    const isGroup = options?.isGroup === true;
    const candidates: RefundCandidate[] = Array.isArray(membership?.refundCandidates)
        ? membership.refundCandidates
        : [];

    // 계보를 끝까지 못 훑었으면 서버가 금액을 안 내린다. 그 상태로 구 API 를 열면 안 된다.
    if (membership?.chainLineageIncomplete === true) {
        return {
            refundBlockMessage:
                '인계 연결이 끊겨 있어 환불할 수 없습니다. 운영팀 확인이 필요합니다.',
            refundCandidates: [],
        };
    }

    // 삭제 원본(소프트 삭제로 종료된 수강권) 또는 탈퇴 회원이면 인계 계보가 없어도
    // 신규 API 를 강제한다 — 위 M3′/P4′ 주석 참고. 그룹은 대상이 아니다(위 옵션 설명).
    const isSoftDeletedOriginal = !isGroup && !!membership?.deletedContext;
    const isWithdrawnMember = !isGroup && isMemberRecordReadOnly(membership);

    // 인계 계보도 없고, 삭제 원본도 탈퇴 회원도 아닌 평범한 수강권만 기존 동작으로 보낸다.
    // "후보가 비어 있다"는 인계 없음과 다르다 — 결제행이 0건이거나 서버가 후보 계산에
    // 실패한 경우도 빈다. 그걸 인계 없음으로 오인하면 구 환불 API 가 열려 인계본의
    // totalPrice 가 음수가 된다. 인계 계보가 있으면 반드시 막는다.
    if (membership?.hasHandoffLineage !== true && !isSoftDeletedOriginal && !isWithdrawnMember) {
        return {};
    }

    if (candidates.length === 0) {
        return {
            refundBlockMessage:
                '이 수강권의 환불 가능 매출을 확인하지 못했습니다. 새로고침 후에도 같으면 운영팀 확인이 필요합니다.',
            refundCandidates: [],
        };
    }

    /**
     * 환불 한도(refundableAmount)는 결제행이 아니라 **수강권 단위**로 계산된다.
     * 그래서 같은 수강권 안에서 어느 행을 고르든 환불 가능액은 같고, 달라지는 것은
     * **매출 귀속(강사·판매담당자)** 뿐이다.
     *
     * 귀속까지 같은 행을 여러 개 늘어놓으면 "1,500,000원" 이 네 줄 뜨는 것처럼
     * 고를 이유도 없고 각각 그만큼 환불되는 것처럼 오해만 준다. 같은 것끼리 묶는다.
     * 원판매(COMPLETED) 행이 있으면 그것을 대표로 쓴다.
     */
    const groups = new Map<string, RefundCandidate>();
    candidates
        .filter((candidate) => candidate?.canRefund === true)
        .forEach((candidate) => {
            const key = [
                candidate.membershipId ?? '',
                candidate.coachId ?? '',
                getSalesManagerName(candidate),
            ].join('|');
            const kept = groups.get(key);
            if (!kept || (kept.saleStatus !== 'COMPLETED' && candidate.saleStatus === 'COMPLETED')) {
                groups.set(key, candidate);
            }
        });
    const refundable = Array.from(groups.values());

    if (refundable.length === 0) {
        // 가장 설명력 있는 사유를 고른다. 전부 같은 사유면 그걸 쓴다.
        const reason =
            candidates.find((candidate) => candidate.blockReason !== 'NOT_ORIGINAL_SALES_ROW')
                ?.blockReason || candidates[0]?.blockReason;
        return { refundBlockMessage: getRefundBlockMessage(reason), refundCandidates: [] };
    }

    if (refundable.length === 1) {
        const only = refundable[0];
        return {
            salesRecordId: only.membershipPaymentId,
            maxRefundAmountOverride: only.refundableAmount ?? 0,
            salesAttributionName: getSalesManagerName(only) || only.coachName || '',
            refundCandidates: refundable,
        };
    }

    return { refundCandidates: refundable };
};
